import axios from 'axios';

// david-service (merged) runs on port 8005. Old david-recruiter (:8006) is retired.
// VITE_DAVID_RECRUITER_URL should NOT include any path prefix (e.g., http://localhost:8005).
const DAVID_SERVICE_BASE = import.meta.env.VITE_DAVID_RECRUITER_URL || 'http://localhost:8005';

// V2 endpoints — LangGraph with intent classification, market_research (Tavily),
// edit_job, and general_chat support. Use for chat text messages.
const DAVID_V2_CHAT = `${DAVID_SERVICE_BASE}/recruiter-ai/v2/chat`;

// V1 endpoints — legacy linear state machine. ONLY used for flows v2 does not
// yet support: file upload, form-based start, and project completion.
// TODO: migrate these to v2 equivalents and remove this constant.
const DAVID_V1_CHAT = `${DAVID_SERVICE_BASE}/api/recruiter-chat`;

/**
 * Conversation stages from david-recruiter service
 */
export enum ConversationStage {
    AWAITING_JOB_DESC = 'awaiting_job_desc',
    INTRODUCTION = 'introduction',
    ROLE_DETAILS_1 = 'role_details_1',
    ROLE_DETAILS_2 = 'role_details_2',
    TRANSITION_TO_REQUIREMENTS = 'transition_to_requirements',
    REQUIREMENTS_1 = 'requirements_1',
    REQUIREMENTS_2 = 'requirements_2',
    TRANSITION_TO_CULTURE = 'transition_to_culture',
    COMPANY_CULTURE = 'company_culture',
    CONFIRMATION = 'confirmation',
    COMPLETE = 'complete'
}

/**
 * Message interface
 */
export interface Message {
    role: 'assistant' | 'user';
    content: string;
    timestamp: string;
}

/**
 * Chat request payload
 */
export interface ChatRequest {
    sessionId: string;
    jobId?: string;
    recruiterId?: string;
    projectId?: string;
    message?: string;
}

/**
 * Entry points — which feature card the recruiter clicked on the talk-to-david
 * page. Drives the personalised greeting on /start and biases classifier.
 */
export type EntryPoint =
    | 'find_candidates'
    | 'create_job'
    | 'review_applicant'
    | 'compare_shortlist'
    | 'free_chat';

/**
 * Suggested next-step chip for quick-action rendering.
 */
export interface SuggestedAction {
    label: string;
    intent: string;
}

/**
 * UI payload attached by v2.1 tool handlers. Drives deep-linking and chips.
 */
export interface UiPayload {
    navigate_to?: string | null;
    suggested_actions?: SuggestedAction[];
    highlights?: Record<string, any>;
}

/**
 * Chat response from david-recruiter (backend sends snake_case)
 */
export interface ChatResponse {
    sessionId: string;  // Mapped from session_id
    message: string;
    stage: ConversationStage;
    completed: boolean;
    jobId?: string;  // Mapped from job_id
    requiresProjectSelection?: boolean;  // Mapped from requires_project_selection
    intent?: string;
    toolUsed?: string;
    structuredData?: Record<string, any>;
    entryPoint?: EntryPoint;
    uiPayload?: UiPayload;
}

/**
 * Raw response from backend (snake_case)
 */
interface RawChatResponse {
    session_id: string;
    message: string;
    stage: ConversationStage;
    completed: boolean;
    job_id?: string;
    requires_project_selection?: boolean;
    intent?: string;
    tool_used?: string;
    structured_data?: Record<string, any>;
    entry_point?: EntryPoint;
    ui_payload?: UiPayload;  // already snake_case on the wire
}

/**
 * Map snake_case response to camelCase
 */
function mapResponse(raw: RawChatResponse): ChatResponse {
    return {
        sessionId: raw.session_id,
        message: raw.message,
        stage: raw.stage,
        completed: raw.completed,
        jobId: raw.job_id,
        requiresProjectSelection: raw.requires_project_selection,
        intent: raw.intent,
        toolUsed: raw.tool_used,
        structuredData: raw.structured_data,
        entryPoint: raw.entry_point,
        uiPayload: raw.ui_payload,
    };
}

/**
 * File upload response
 */
export interface FileUploadResponse {
    sessionId: string;
    message: string;
    fileName: string;
    fileSize: number;
    jobId?: string;  // Job ID returned from job-service after PDF upload
}

/**
 * One persisted message returned by GET /v2/chat/session/{id} (Slice 4.4a).
 * Matches the backend V2SessionMessage Pydantic model shape.
 */
export interface SessionRehydrateMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string; // ISO datetime
    ui_payload?: UiPayload | null;  // Slice 4.4a — per-message structured output
    tool_used?: string | null;
}

/**
 * Backend V2SessionRehydrate shape (snake_case wire format).
 */
interface RawSessionRehydrate {
    session_id: string;
    recruiter_id?: string | null;
    company_id?: string | null;
    entry_point?: EntryPoint | null;
    intent?: string | null;
    tool_used?: string | null;
    stage?: ConversationStage | null;
    messages: SessionRehydrateMessage[];
    ui_payload?: UiPayload | null;
    last_shown_jobs?: any[];
    last_shown_candidates?: any[];
    selected_job_id?: string | null;
    selected_candidate_id?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

/**
 * Frontend-friendly rehydrate shape (camelCase). Returned by
 * davidApi.getSession() and consumed by TalkToDavid's restore-on-mount.
 */
export interface SessionRehydrate {
    sessionId: string;
    entryPoint?: EntryPoint;
    intent?: string;
    toolUsed?: string;
    stage?: ConversationStage;
    messages: SessionRehydrateMessage[];
    uiPayload?: UiPayload;
    lastShownJobs: any[];
    lastShownCandidates: any[];
    selectedJobId?: string;
    selectedCandidateId?: string;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Slice 4.6 — backend V2SessionSummary shape (snake_case wire).
 */
interface RawSessionSummary {
    session_id: string;
    entry_point?: EntryPoint | null;
    intent?: string | null;
    last_message_preview?: string | null;
    message_count: number;
    updated_at?: string | null;
    created_at?: string | null;
}

interface RawSessionsList {
    sessions: RawSessionSummary[];
    count: number;
}

/**
 * Frontend-friendly session summary for the Past Chats panel.
 */
export interface SessionSummary {
    sessionId: string;
    entryPoint?: EntryPoint;
    intent?: string;
    lastMessagePreview?: string;
    messageCount: number;
    updatedAt?: string;
    createdAt?: string;
}

function mapSessionSummary(raw: RawSessionSummary): SessionSummary {
    return {
        sessionId: raw.session_id,
        entryPoint: raw.entry_point ?? undefined,
        intent: raw.intent ?? undefined,
        lastMessagePreview: raw.last_message_preview ?? undefined,
        messageCount: raw.message_count ?? 0,
        updatedAt: raw.updated_at ?? undefined,
        createdAt: raw.created_at ?? undefined,
    };
}

function mapSessionRehydrate(raw: RawSessionRehydrate): SessionRehydrate {
    return {
        sessionId: raw.session_id,
        entryPoint: raw.entry_point ?? undefined,
        intent: raw.intent ?? undefined,
        toolUsed: raw.tool_used ?? undefined,
        stage: raw.stage ?? undefined,
        messages: raw.messages || [],
        uiPayload: raw.ui_payload ?? undefined,
        lastShownJobs: raw.last_shown_jobs || [],
        lastShownCandidates: raw.last_shown_candidates || [],
        selectedJobId: raw.selected_job_id ?? undefined,
        selectedCandidateId: raw.selected_candidate_id ?? undefined,
        createdAt: raw.created_at ?? undefined,
        updatedAt: raw.updated_at ?? undefined,
    };
}

/**
 * David AI Recruiter API Service
 * Handles all interactions with the david-recruiter backend
 */
class DavidApiService {
    /** Get Authorization header from localStorage */
    private getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = {};
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
        }
        return headers;
    }

    /**
     * Start a new chat session with David
     * @param sessionId - Unique session identifier
     * @param recruiterId - Optional recruiter ID for tracking
     * @param jobId - Optional job ID if refining existing job
     * @returns Promise<ChatResponse>
     */
    async startChat(
        sessionId: string,
        _recruiterId?: string,
        _jobId?: string,
        projectId?: string,
        entryPoint?: EntryPoint,
        selectedJobId?: string,
    ): Promise<ChatResponse> {
        try {
            // V2 endpoint is JWT-authenticated — recruiter_id/company_id are resolved
            // from the token server-side. Only send session_id and optional project_id.
            const payload: Record<string, unknown> = {
                session_id: sessionId,
                project_id: projectId,
            };
            if (entryPoint) payload.entry_point = entryPoint;
            if (selectedJobId) payload.selected_job_id = selectedJobId;

            const response = await axios.post<RawChatResponse>(
                `${DAVID_V2_CHAT}/start`,
                payload,
                { headers: this.getAuthHeaders() },
            );

            return mapResponse(response.data);
        } catch (error) {
            console.error('Error starting chat with David (v2):', error);
            throw error;
        }
    }

    /**
     * Send a message to David in an ongoing conversation
     * @param sessionId - Session identifier
     * @param message - User's message
     * @param recruiterId - Optional recruiter ID
     * @returns Promise<ChatResponse>
     */
    /**
     * Slice 4.6 — list the recruiter's recent v2 sessions for the Past Chats panel.
     *
     * Returns summary metadata only (no full message arrays — those come from
     * getSession when the user clicks an entry). Default limit is 10, hard
     * cap is 50 server-side.
     */
    async listSessions(limit: number = 10): Promise<SessionSummary[]> {
        try {
            const response = await axios.get<RawSessionsList>(
                `${DAVID_V2_CHAT}/sessions`,
                { params: { limit }, headers: this.getAuthHeaders() },
            );
            return (response.data?.sessions || []).map(mapSessionSummary);
        } catch (error) {
            console.error('Error listing sessions (v2):', error);
            throw error;
        }
    }

    /**
     * Slice 4.4a — fetch the persisted session for restore-on-mount.
     *
     * Returns the rehydrate shape if the session exists, or `null` for 404
     * (frontend should treat that as "stale localStorage → start a fresh
     * session"). Other errors propagate so the caller can decide whether to
     * retry or surface them.
     */
    async getSession(sessionId: string): Promise<SessionRehydrate | null> {
        try {
            const response = await axios.get<RawSessionRehydrate>(
                `${DAVID_V2_CHAT}/session/${encodeURIComponent(sessionId)}`,
                { headers: this.getAuthHeaders() },
            );
            return mapSessionRehydrate(response.data);
        } catch (error: any) {
            if (error?.response?.status === 404) return null;
            console.error('Error fetching session (v2):', error);
            throw error;
        }
    }

    async sendMessage(
        sessionId: string,
        message: string,
        _recruiterId?: string,
        selectedJobId?: string,
        forcedIntent?: string,
    ): Promise<ChatResponse> {
        try {
            // V2 classifies intent (general_chat, market_research, edit_job, etc.)
            // and handles off-topic messages gracefully instead of treating every
            // input as a job description. selectedJobId + forcedIntent let the UI
            // bind the conversation to a specific job AND skip the LLM classifier
            // for deterministic chip/button actions.
            const payload: Record<string, unknown> = {
                session_id: sessionId,
                message,
            };
            if (selectedJobId) payload.selected_job_id = selectedJobId;
            if (forcedIntent) payload.forced_intent = forcedIntent;

            const response = await axios.post<RawChatResponse>(
                `${DAVID_V2_CHAT}/message`,
                payload,
                { headers: this.getAuthHeaders() },
            );

            return mapResponse(response.data);
        } catch (error) {
            console.error('Error sending message to David (v2):', error);
            throw error;
        }
    }

    /**
     * Start a chat session from pre-filled form data with full LLM processing
     * This processes form data the same way as PDF uploads - with LLM analysis
     * @param formData - Job posting form data
     * @param recruiterId - Optional recruiter ID
     * @param projectId - Optional project ID
     * @param userRole - Optional user role (company, recruiter, candidate)
     * @param companyId - Optional company ID from localStorage
     * @returns Promise<ChatResponse>
     */
    async startFromFormWithLLM(
        sessionId: string,
        formData: Record<string, any>,
        recruiterId?: string,
        projectId?: string,
        userRole?: string,
        companyId?: string
    ): Promise<ChatResponse> {
        try {
            // Backend expects snake_case in the request body (same as startChat)
            const payload = {
                session_id: sessionId,
                form_data: formData,
                recruiter_id: recruiterId,
                project_id: projectId,
                user_role: userRole,
                company_id_from_storage: companyId
            };


            const response = await axios.post<RawChatResponse>(
                `${DAVID_V1_CHAT}/start-from-form-with-llm`,
                payload,
                { headers: this.getAuthHeaders() }
            );


            return mapResponse(response.data);
        } catch (error) {
            console.error('Error starting chat from form with LLM:', error);
            throw error;
        }
    }

    /**
     * Upload a job description file
     * @param sessionId - Session identifier
     * @param file - File to upload (PDF, DOCX, TXT, DOC)
     * @param recruiterId - Optional recruiter ID
     * @returns Promise<FileUploadResponse>
     */
    async uploadJobDescription(
        sessionId: string,
        file: File,
        recruiterId?: string,
        projectId?: string,
        userRole?: string,
        companyId?: string
    ): Promise<FileUploadResponse> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            // Backend expects snake_case for Form fields
            formData.append('session_id', sessionId);
            if (recruiterId) {
                formData.append('recruiter_id', recruiterId);
            }
            if (projectId) {
                formData.append('project_id', projectId);
            }
            if (userRole) {
                formData.append('user_role', userRole);
            }
            if (companyId) {
                formData.append('company_id', companyId);
            }

            // Get auth token for authorization
            const authToken = localStorage.getItem('authToken');
            const headers: Record<string, string> = {
                'Content-Type': 'multipart/form-data'
            };
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }

            const response = await axios.post<FileUploadResponse>(
                `${DAVID_V1_CHAT}/upload-job-description`,
                formData,
                { headers }
            );

            return response.data;
        } catch (error) {
            console.error('Error uploading job description:', error);
            throw error;
        }
    }

    /**
     * Generate a unique session ID
     * @returns string - UUID v4
     */
    generateSessionId(): string {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Complete conversation with project selection and create job
     * This is called when user selects a project at the end of the conversation
     * @param sessionId - Session identifier
     * @param projectId - Selected project ID
     * @returns Promise with job_id and success status
     */
    async completeWithProject(
        sessionId: string,
        projectId: string
    ): Promise<{ success: boolean; job_id: string | null; message: string }> {
        try {
            const formData = new FormData();
            formData.append('session_id', sessionId);
            formData.append('project_id', projectId);


            // Get auth token for authorization (required by backend)
            const authToken = localStorage.getItem('authToken');
            const headers: Record<string, string> = {
                'Content-Type': 'multipart/form-data'
            };
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }

            const response = await axios.post(
                `${DAVID_V1_CHAT}/complete-with-project`,
                formData,
                { headers }
            );

            return response.data;
        } catch (error) {
            console.error('Error completing conversation with project:', error);
            throw error;
        }
    }

    /**
     * Check if david-recruiter service is healthy
     * @returns Promise<boolean>
     */
    async healthCheck(): Promise<boolean> {
        try {
            const response = await axios.get(`${DAVID_SERVICE_BASE}/health`);
            return response.data.status === 'healthy';
        } catch (error) {
            console.error('David service health check failed:', error);
            return false;
        }
    }
}

export const davidApiService = new DavidApiService();
